import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-semantic-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-2',
  {
    variants: {
      variant: {
        default: 'border-transparent text-primary-foreground hover:opacity-90',
        destructive: 'border-transparent text-destructive-foreground hover:opacity-90',
        outline: 'border-border bg-transparent hover:bg-accent',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:opacity-90',
        ghost: 'border-transparent hover:bg-accent',
        link: 'border-transparent text-brand underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const effectiveVariant = variant ?? 'default'
    // Solid-color variants set background/text color via inline style rather than
    // Tailwind utility classes alone, because the brand-derived colors used here are
    // intentionally distinct from (and, for "link", higher-contrast than) the plain
    // design tokens the className would otherwise resolve to - see the --color-brand /
    // --color-brand-button comments in index.css for the WCAG 1.4.3 rationale.
    const variantStyle: React.CSSProperties | undefined =
      effectiveVariant === 'default'
        ? { backgroundColor: 'var(--color-brand-button)' }
        : effectiveVariant === 'destructive'
          ? { backgroundColor: 'hsl(var(--destructive))' }
          : effectiveVariant === 'link'
            ? { color: 'var(--color-brand-button)' }
            : undefined
    const computedStyle = { ...variantStyle, ...style }
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={computedStyle}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
